import { DOCUMENT, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, input, inject } from '@angular/core';
import { ActivatedRoute, UrlSegment } from '@angular/router';

import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
} from 'rxjs';

import { ConsentService } from '@fcom/common/login/services/consent.service';
import { QualtricsSurvey, QualtricsSurveys, getQualtricsUrl } from '@fcom/common/qualtrics/qualtrics.utils';
import type { AppState } from '@fcom/core';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors/login.selector';
import type { Profile } from '@fcom/core-api/login/interfaces/login.interface';
import { LanguageService } from '@fcom/ui-translate';
import { ConsentTextId } from '@fcom/common';
import { PageViewType } from '@fcom/core-api';

@Component({
  selector: 'fin-qualtrics-embedded-page-feedback',
  templateUrl: './qualtrics-embedded-page-feedback.component.html',
  styleUrl: './qualtrics-embedded-page-feedback.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QualtricsEmbeddedPageFeedbackComponent implements OnInit {
  private languageService = inject(LanguageService);
  private activatedRoute = inject(ActivatedRoute);
  private consentService = inject(ConsentService);
  private document = inject<Document>(DOCUMENT);
  private location = inject(Location);
  private store$ = inject<Store<AppState>>(Store);

  readonly PageViewType = PageViewType;
  readonly survey: QualtricsSurvey = QualtricsSurveys.PAGE_FEEDBACK;

  private survey$: BehaviorSubject<QualtricsSurvey>;

  pageType = input<string>('');
  surveyName = computed<string>(() => this.survey.name);
  classByPageType = computed<string>(() => {
    switch (this.pageType()) {
      case PageViewType.BLUEWINGS_LANDING_PAGE:
      case PageViewType.EDITORIAL_LANDING_PAGE:
        return 'md-col-4-12 sm-col-2-11 mr-xxsmall-l';
      case PageViewType.EDITORIAL_CATEGORY_PAGE:
        return 'md-col-4-12 sm-col-2-11 mr-xxsmall-l mr-xxlarge-t';
      case PageViewType.PREMIUM_ECONOMY_LANDING_PAGE_2021:
        return 'md-col-4-12 sm-col-2-11 pr-large-t';
      case PageViewType.SECTION_PAGE_WITH_TITLE:
        return 'md-col-5-12 pr-large-b';
      case 'full': // manually set pageType in timetables component
        return 'mr-clear-l mr-clear-r mr-large-t';
      case 'myFplus': // manually set pageType in my-finnair-plus
        // No margin: preceding card already sets the standard card-gap.
        return 'col-1-12 md-col-4-12';
      case PageViewType.NO_GRID_FULL_WIDTH_PAGE_WITH_TITLE:
      case PageViewType.NO_GRID_FULL_WIDTH_PAGE:
      default:
        return 'flex--center mr-xlarge-t';
    }
  });

  qualtricsUrl$: Observable<string>;
  consentGiven$: Observable<boolean>;

  showFeedback = false;

  ngOnInit(): void {
    this.survey$ = new BehaviorSubject<QualtricsSurvey>(this.survey);

    const profile$ = this.store$.pipe(profileOrUndefinedIfNotLoggedIn(), startWith(undefined as Profile | undefined));
    this.qualtricsUrl$ = combineLatest([
      this.survey$.pipe(
        filter(Boolean),
        distinctUntilChanged((a, b): boolean => a.name === b.name && a.url === b.url)
      ),
      profile$,
    ]).pipe(
      switchMap(([survey, profile]): Observable<string> =>
        this.languageService.translate(survey.url).pipe(
          filter(Boolean),
          map(
            (url): string =>
              getQualtricsUrl(
                this.languageService.langKeyValue,
                this.languageService.langValue,
                url,
                undefined,
                undefined,
                profile
              ) +
              `&current_page_url=${this.location.path()}&pagePath=${this.strippedPath()}&locale=${this.languageService.ddsLocaleValue.countrySite}`
          )
        )
      )
    );

    this.consentGiven$ = this.consentService
      .getCookieConsentStatusById(ConsentTextId.COOKIE_ANALYTICS)
      .pipe(map(Boolean), distinctUntilChanged());

    this.showFeedback = this.checkVisibility();
  }

  strippedPath(): string {
    const path: string = this.location.path();
    const stripParams: string = path.split('?')[0];
    const splitPath: string[] = stripParams.split('/');
    if (path.includes('/int/gb/') || splitPath.length <= 2) {
      return path;
    }
    return splitPath.slice(2).join('/');
  }

  checkVisibility(): boolean {
    const pathUrl: string = this.location.path();
    const activatedRouteSnapshot: UrlSegment[] = this.activatedRoute.snapshot?.url;

    // if frontpage && NOT timetables (was being evaluated as frontpage)
    if (
      activatedRouteSnapshot?.length < 1 &&
      !(pathUrl.includes('timetables') || pathUrl.includes('my-finnair-plus') || pathUrl.includes('upgrade'))
    ) {
      return false;
    }

    // hide for given paths
    if (this.hideFeedback(pathUrl)) {
      return false;
    }

    // hide for 404 page

    if (pathUrl.includes('404-not-found')) {
      return false;
    }

    return true;
  }

  private hideFeedback(pathUrl: string): boolean {
    //parse through alternateUrls to remove feedback on page for all languages
    const alternateUrl: HTMLElement = this.document?.querySelector('link[hreflang="en-GB"]');
    const alternateUrlHref: string = alternateUrl?.getAttribute('href');

    return [
      'flash-sale',
      'finnair100',
      'offers',
      'special-offers',
      'business-class-flights',
      'customer-care-and-contact-information',
      'newsletter',
      'compensation-wizard',
      'contact-finnair-corporate-programme',
      'join-finnair-corporate-programme',
      'claim-missing-avios',
      'contact-and-request-forms',
      'incident-updates',
    ].some((page) => pathUrl.includes(page) || alternateUrlHref?.includes(page));
  }
}
